import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, EyeIcon, EyeOffIcon, MailCheckIcon } from 'lucide-react';
import { Spinner } from '../../ui/Spinner';
import { useAccount } from '../../contexts/AccountContext';
import { PASSWORD_HINT, PASSWORD_MAX, PASSWORD_MIN, passwordProblem } from '../../core/rules/password';
import { isValidEmail } from '../../core/rules/contact';
import { legalLinks } from '../../data/legal';
import { hasBackend } from '../../utils/api';
import { demoLogins } from '../../utils/accountApi';
import { useSite } from '../../contexts/SiteContext';
import { btnPrimary, btnSecondary, inputCls, labelCls } from '../../ui/styles';
import { ease } from '../../core/format/motion';
import { DemoLoginPicker } from './DemoLoginPicker';
import { AuthAside } from './AuthAside';
import { AuthResend } from './AuthResend';
type Mode = 'login' | 'register' | 'reset' | 'reset-sent' | 'magic-sent';
type Result = {
  ok: boolean;
  error?: string;
};
const copy: Record<Mode, {
  title: string;
  lead: string;
}> = {
  login: {
    title: 'Welkom terug.',
    lead: 'Je huidplan, leveringen en afspraken op één plek.'
  },
  register: {
    title: 'Maak je account.',
    lead: 'Dan bewaar je je huidplan en regel je leveringen zelf, wanneer het jou uitkomt.'
  },
  reset: {
    title: 'Nieuw wachtwoord.',
    lead: 'Vul je e-mailadres in. Je krijgt een link om een nieuw wachtwoord te kiezen.'
  },
  'reset-sent': {
    title: 'Check je inbox.',
    lead: ''
  },
  'magic-sent': {
    title: 'Check je inbox.',
    lead: ''
  }
};
const submitLabel: Record<'login' | 'register' | 'reset', string> = {
  login: 'Inloggen',
  register: 'Account maken',
  reset: 'Stuur de link'
};
const ERROR_ID = 'auth-error';
const PASSWORD_HINT_ID = 'auth-password-hint';
type Field = 'name' | 'email' | 'password';
type FieldErrors = Partial<Record<Field, string>>;
const fieldErrorId = (f: Field) => `auth-${f}-error`;

/** Controle vóór versturen: de fout staat onder het veld waar hij over gaat. */
function checkFields(mode: Mode, v: {
  firstName: string;
  email: string;
  password: string;
}, emailOnly = false): FieldErrors {
  const e: FieldErrors = {};
  if (mode === 'register' && !emailOnly && !v.firstName.trim()) e.name = 'Vul je voornaam in.';
  if (!v.email.trim()) e.email = 'Vul je e-mailadres in.';else if (!isValidEmail(v.email)) e.email = 'Dit e-mailadres klopt nog niet helemaal.';
  if (!emailOnly && mode === 'login' && !v.password) e.password = 'Vul je wachtwoord in.';
  if (!emailOnly && mode === 'register') {
    const problem = passwordProblem(v.password, {
      email: v.email,
      firstName: v.firstName
    });
    if (problem) e.password = problem;
  }
  return e;
}
const tabCls = (on: boolean) => `flex-1 whitespace-nowrap rounded-full px-4 py-2.5 text-[14px] transition-colors duration-150 ease-soft ${on ? 'bg-ink text-shell' : 'text-muted hover:text-ink'}`;

/** Inloggen, registreren en wachtwoord herstellen — één scherm, geen doolhof. */
export function AccountAuth({
  initialMode = 'login'
}: {
  initialMode?: 'login' | 'reset';
}) {
  const {
    policy
  } = useSite();
  const {
    signIn,
    sendMagicLink,
    openMagicLink,
    requestReset,
    openResetLink,
    register,
    linkNotice,
    dismissLinkNotice
  } = useAccount();
  const reduce = useReducedMotion();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState<null | 'main' | 'magic'>(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Bij elke wissel van scherm (niet bij het eerste openen) de focus op de kop: zo hoort een
  // schermlezer dat ze nu bij "Nieuw wachtwoord." of "Check je inbox." is, net als bij OrderReturn.
  // Wisselen tussen de tabs Inloggen en Account maken laat de focus op de tab: daar wil ze verder.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const switched = useRef(false);
  const prevMode = useRef(mode);
  useEffect(() => {
    const tabs = (m: Mode) => m === 'login' || m === 'register';
    if (switched.current && !(tabs(prevMode.current) && tabs(mode))) headingRef.current?.focus({
      preventScroll: true
    });
    switched.current = true;
    prevMode.current = mode;
  }, [mode]);
  const go = (m: Mode) => {
    setError('');
    setFieldErrors({});
    if (m !== 'login') dismissLinkNotice();
    setMode(m);
  };
  // Na een verlopen inloglink: het e-mailveld meteen klaar, zodat ze direct een nieuwe aanvraagt.
  const showLinkNotice = mode === 'login' && linkNotice !== null;
  const run = async (kind: 'main' | 'magic', action: () => Promise<Result>, next?: Mode) => {
    setBusy(kind);
    setError('');
    const r = await action();
    setBusy(null);
    if (!r.ok) setError(r.error ?? 'Dat lukte niet. Controleer je verbinding en probeer het opnieuw.');else if (next) setMode(next);
  };

  /** Klopt alles? Zo niet: fouten onder de velden en de focus op het eerste foute veld. */
  const valid = (emailOnly = false) => {
    const found = checkFields(mode, {
      firstName,
      email,
      password
    }, emailOnly);
    setFieldErrors(found);
    const order: Field[] = ['name', 'email', 'password'];
    const first = order.find(f => found[f]);
    if (first) {
      document.getElementById(`auth-${first}`)?.focus();
      return false;
    }
    return true;
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid(mode === 'reset')) return;
    if (mode === 'login') void run('main', () => signIn(email, password));
    if (mode === 'register') void run('main', () => register({
      firstName,
      email,
      password
    }));
    if (mode === 'reset') void run('main', () => requestReset(email), 'reset-sent');
  };
  const sent = mode === 'reset-sent' || mode === 'magic-sent';
  /** Per veld: ongeldig bij een eigen fout of bij een serverfout (die gaat over de combinatie). */
  const invalidFor = (f: Field) => fieldErrors[f] || error ? true : undefined;
  const describedFor = (f: Field, extra?: string) => [extra ?? '', fieldErrors[f] ? fieldErrorId(f) : '', error ? ERROR_ID : ''].filter(Boolean).join(' ') || undefined;
  const FieldError = ({
    field
  }: {
    field: Field;
  }) => fieldErrors[field] ? <p id={fieldErrorId(field)} className="mt-1.5 text-[13px] text-clay" data-id="element-5894" data-mp="2z8whaoq3gb52">
        {fieldErrors[field]}
      </p> : null;
  const setField = (f: Field, set: (v: string) => void) => (v: string) => {
    set(v);
    if (fieldErrors[f]) setFieldErrors(e => ({
      ...e,
      [f]: undefined
    }));
  };
  return <section className="mx-auto grid max-w-content gap-12 px-5 py-12 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-20" data-id="element-5895" data-mp="3cz9ub5hueg19">
      <div className="lg:col-span-5 lg:col-start-2" data-id="element-5896" data-mp="3i0pe6u8aagi2">
        <AnimatePresence mode="wait" initial={false} data-id="element-5897" data-mp="21jl6yd0ofezx">
          <motion.div key={mode} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.2,
          ease
        }} data-id="element-5898" data-mp="26jpn21xoa7pm">
            {(mode === 'reset' || sent) && <button type="button" onClick={() => go('login')} className="mb-4 inline-flex min-h-[44px] items-center gap-1.5 text-[14px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-5899" data-mp="xn26tyzh86y1">
                <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5900" data-mp="wrwcol3i5gu" />
                Terug naar inloggen
              </button>}

            {sent && <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-ink text-shell" aria-hidden="true" data-id="element-5901" data-mp="epr8cglo5hjn">
                <MailCheckIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-5902" data-mp="3ic84wym9naea" />
              </span>}

            <h1 ref={headingRef} tabIndex={-1} className="font-display text-[2.4rem] font-light leading-[1.05] text-ink outline-none sm:text-5xl" data-id="element-5903" data-mp="3ec7nr67s8l3j">
              {copy[mode].title}
            </h1>

            {mode === 'magic-sent' && <div className="mt-4 space-y-5 text-[16px] leading-relaxed text-muted" role="status" data-id="element-5904" data-mp="c05wppk6myvz">
                <p data-id="element-5905" data-mp="g3luip7zn5a9">
                  We stuurden een inloglink naar <span className="text-ink" data-id="element-5906" data-mp="l0ucopt4z9bi">{email}</span>. Klik erop en je bent binnen, zonder wachtwoord. De link werkt{' '}
                  {policy.magicLinkMinutes} minuten.
                </p>
                {!hasBackend && <button type="button" onClick={() => void openMagicLink(email)} className={btnPrimary} data-id="element-5907" data-mp="uwxetvnzn7yg">
                    Open de link (demo)
                  </button>}
                <AuthResend onResend={() => sendMagicLink(email)} onChangeEmail={() => go('login')} data-id="element-5908" data-mp="13qk73bszlt2w" />
              </div>}
            {mode === 'reset-sent' && <div className="mt-4 space-y-5 text-[16px] leading-relaxed text-muted" role="status" data-id="element-5909" data-mp="1dogdjqazbzuo">
                <p data-id="element-5910" data-mp="1nu7yqnxjywrp">
                  Staat <span className="text-ink" data-id="element-5911" data-mp="1nurepz3n7b33">{email}</span> bij ons bekend, dan staat er binnen een minuut een mail klaar. Niets gekregen? Kijk even in
                  je spam.
                </p>
                {!hasBackend && <button type="button" onClick={() => openResetLink(email)} className={btnPrimary} data-id="element-5912" data-mp="19tf29xnoxrly">
                    Open de link uit de mail (demo)
                  </button>}
                <AuthResend onResend={() => requestReset(email)} onChangeEmail={() => go('reset')} data-id="element-5913" data-mp="21kl6ap6j89gi" />
              </div>}

            {!sent && <>
                <p className="mt-4 max-w-md text-[16px] leading-relaxed text-muted" data-id="element-5914" data-mp="1wrs1k1b2d14f">{copy[mode].lead}</p>
                {showLinkNotice && <p role="alert" className="mt-6 max-w-md bg-blush p-4 text-[15px] leading-relaxed text-ink" data-id="element-5915" data-mp="2papaoyrjod8k">
                    {linkNotice}
                  </p>}

                {mode !== 'reset' && <div role="tablist" aria-label="Inloggen of account maken" className="mt-8 flex max-w-sm rounded-full bg-sand p-1" data-id="element-5916" data-mp="3u654bs04z6cp">
                    <button type="button" role="tab" aria-selected={mode === 'login'} onClick={() => mode !== 'login' && go('login')} className={tabCls(mode === 'login')} data-id="element-5917" data-mp="3p8c21a1yewhp">
                      Inloggen
                    </button>
                    <button type="button" role="tab" aria-selected={mode === 'register'} onClick={() => mode !== 'register' && go('register')} className={tabCls(mode === 'register')} data-id="element-5918" data-mp="gyaf217uimad">
                      Account maken
                    </button>
                  </div>}

                <form onSubmit={onSubmit} noValidate className={`${mode === 'reset' ? 'mt-8' : 'mt-6'} space-y-4`} data-id="element-5919" data-mp="lpsg0p9vcmvc">
                  {mode === 'register' && <div data-id="element-5920" data-mp="zpl227agaewz">
                      <label htmlFor="auth-name" className={labelCls} data-id="element-5921" data-mp="uq8ipwisfpz5">
                        Voornaam
                      </label>
                      <input id="auth-name" autoComplete="given-name" value={firstName} onChange={e => setField('name', setFirstName)(e.target.value)} aria-invalid={fieldErrors.name ? true : undefined} aria-describedby={fieldErrors.name ? fieldErrorId('name') : undefined} className={inputCls} data-id="element-5922" data-mp="1ebqr1dhdt5zd" />
                      <FieldError field="name" data-id="element-5923" data-mp="10cr7gk8q7ha2" />
                    </div>}
                  <div data-id="element-5924" data-mp="lnv0kqieoab1">
                    <label htmlFor="auth-email" className={labelCls} data-id="element-5925" data-mp="loe6f8us788p">
                      E-mailadres
                    </label>
                    <input id="auth-email" type="email" inputMode="email" autoComplete="email" autoFocus={showLinkNotice} value={email} onChange={e => setField('email', setEmail)(e.target.value)} aria-invalid={invalidFor('email')} aria-describedby={describedFor('email')} className={inputCls} data-id="element-5926" data-mp="qkbnjhdvcimj" />
                    <FieldError field="email" data-id="element-5927" data-mp="24maozh8555d5" />
                  </div>
                  {mode !== 'reset' && <div data-id="element-5928" data-mp="32a1ckphhjmuz">
                      <div className="flex items-baseline justify-between" data-id="element-5929" data-mp="3299ftbc5jj34">
                        <label htmlFor="auth-password" className={labelCls} data-id="element-5930" data-mp="2xaew876d1qhy">
                          {mode === 'register' ? 'Kies een wachtwoord' : 'Wachtwoord'}
                        </label>
                        {mode === 'login' && <button type="button" onClick={() => go('reset')} className="text-[13px] text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-5931" data-mp="2oc2yzmaznugj">
                            Wachtwoord vergeten?
                          </button>}
                      </div>
                      <div className="relative" data-id="element-5932" data-mp="1d8pu485kqo3">
                        <input id="auth-password" type={show ? 'text' : 'password'} autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={mode === 'register' ? PASSWORD_MIN : undefined} maxLength={PASSWORD_MAX} value={password} onChange={e => setField('password', setPassword)(e.target.value)} aria-invalid={invalidFor('password')} aria-describedby={describedFor('password', mode === 'register' ? PASSWORD_HINT_ID : undefined)} className={`${inputCls} pr-12`} data-id="element-5933" data-mp="1be6uvgfz8mn" />
                        <button type="button" onClick={() => setShow(v => !v)} aria-label="Wachtwoord tonen" aria-pressed={show} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-5934" data-mp="1vios78f0uu0f">
                          {show ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5935" data-mp="17up4yigx5lql" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5936" data-mp="17skol11u2u9f" />}
                        </button>
                      </div>
                      <FieldError field="password" data-id="element-5937" data-mp="1hsl92qbfvgf7" />
                      {mode === 'register' && !fieldErrors.password && <p id={PASSWORD_HINT_ID} className="mt-1.5 text-[13px] text-muted" data-id="element-5938" data-mp="u1pp2fd85pii">
                          {PASSWORD_HINT}
                        </p>}
                    </div>}

                  <AnimatePresence initial={false} data-id="element-5939" data-mp="30ppz5wqhzvxl">
                    {error && <motion.p id={ERROR_ID} role="alert" initial={reduce ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={reduce ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.18,
                  ease
                }} className="overflow-hidden text-[14px] text-clay" data-id="element-5940" data-mp="35kceuc3m4anh">
                        {error}
                      </motion.p>}
                  </AnimatePresence>

                  <button type="submit" disabled={busy !== null} className={`${btnPrimary} w-full py-3.5`} data-id="element-5941" data-mp="1z2764kn23oem">
                    {busy === 'main' && <Spinner data-id="element-5942" data-mp="2340y84j6rk36" />}
                    {submitLabel[mode]}
                  </button>

                  {mode === 'login' && <>
                      <div className="flex items-center gap-4 py-1 text-[13px] text-muted" aria-hidden="true" data-id="element-5943" data-mp="4lyv7ukhtjfk">
                        <span className="h-px flex-1 bg-line" data-id="element-5944" data-mp="3w0czpslf5k61" />
                        of
                        <span className="h-px flex-1 bg-line" data-id="element-5945" data-mp="3r1tejuengceq" />
                      </div>
                      <button type="button" disabled={busy !== null} onClick={() => {
                  if (valid(true)) void run('magic', () => sendMagicLink(email), 'magic-sent');
                }} aria-describedby="auth-magic-hint" className={`${btnSecondary} w-full py-3.5`} data-id="element-5946" data-mp="3mzye0s4jm320">
                        {busy === 'magic' && <Spinner data-id="element-5947" data-mp="1b9vt6fu8oksc" />}
                        Stuur me een inloglink
                      </button>
                      <p id="auth-magic-hint" className="text-center text-[13px] text-muted" data-id="element-5948" data-mp="16c43ztkzbcru">
                        Geen wachtwoord nodig: je klikt op een link in je mail.
                      </p>
                    </>}

                  {mode === 'register' && <p className="text-[13px] leading-relaxed text-muted" data-id="element-5949" data-mp="1nl6j476y4wd8">
                      We gebruiken je gegevens alleen voor je bestellingen, afspraken en huidadvies. Lees het{' '}
                      <Link to={legalLinks.privacy} className="text-ink underline underline-offset-4" data-id="element-5950" data-mp="vv1t9t0f3h4m">
                        privacybeleid
                      </Link>
                      .
                    </p>}
                </form>

                {mode === 'login' && demoLogins && <DemoLoginPicker logins={demoLogins} selectedEmail={email} onPick={(e, p) => {
              setEmail(e);
              setPassword(p);
              setError('');
            }} data-id="element-5951" data-mp="19ujp2sc4runh" />}
              </>}
          </motion.div>
        </AnimatePresence>
      </div>

      <AuthAside data-id="element-5952" data-mp="328rndxpbr56t" />
    </section>;
}